/**
 * @file Persistence for upload session history (localStorage) and multipart contexts (IndexedDB).
 */

import { UPLOAD_SESSION_RETENTION_MS } from './constants';
import {
  isUploadSessionWithinRetention,
  pruneSessions,
} from './upload-manager-reducer';

import type { PersistedMultipartContext, UploadResourceSession } from './types';

const DB_NAME = 'surface-upload-sessions';
const DB_VERSION = 1;
const STORE_NAME = 'multipart-contexts';

/** localStorage key for Transfers Uploads history (UI state, not binary file data). */
export const UPLOAD_SESSIONS_STORAGE_KEY =
  'surface.file-transfers.upload-sessions.v1';

type PersistedUploadSessionsPayload = {
  version: 1;
  sessions: UploadResourceSession[];
};

const isUploadResourceSession = (
  value: unknown
): value is UploadResourceSession => {
  if (value == null || typeof value !== 'object') {
    return false;
  }
  const session = value as Partial<UploadResourceSession>;
  return (
    typeof session.id === 'string' &&
    typeof session.kind === 'string' &&
    typeof session.label === 'string' &&
    typeof session.returnPath === 'string' &&
    typeof session.dismissed === 'boolean' &&
    Array.isArray(session.files)
  );
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () =>
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'uppyFileId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });

const withStore = async <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | Promise<T>
): Promise<T> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const result = fn(store);

    if (result instanceof Promise) {
      result.then(resolve).catch(reject);
      return;
    }

    result.onsuccess = () => resolve(result.result);
    result.onerror = () =>
      reject(result.error ?? new Error('IndexedDB request failed'));
    transaction.oncomplete = () => db.close();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('IndexedDB transaction failed'));
  });
};

/** Saves S3 multipart registration context to IndexedDB for resume after reload. */
export const saveMultipartContext = async (
  context: PersistedMultipartContext
): Promise<void> => {
  await withStore('readwrite', (store) => store.put(context));
};

/** Removes multipart context from IndexedDB after finalize or cancel. */
export const deleteMultipartContext = async (
  uppyFileId: string
): Promise<void> => {
  await withStore('readwrite', (store) => store.delete(uppyFileId));
};

/** Loads all persisted multipart contexts from IndexedDB on provider mount. */
export const loadAllMultipartContexts = async (): Promise<
  PersistedMultipartContext[]
> => {
  return withStore('readonly', (store) => store.getAll());
};

/**
 * Loads upload session history for the Transfers Uploads tab.
 * Completed sessions older than {@link UPLOAD_SESSION_RETENTION_MS} are dropped.
 */
export const loadPersistedUploadSessions = (): UploadResourceSession[] => {
  if (typeof localStorage === 'undefined') {
    return [];
  }

  try {
    const raw = localStorage.getItem(UPLOAD_SESSIONS_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as PersistedUploadSessionsPayload;
    if (parsed.version !== 1 || !Array.isArray(parsed.sessions)) {
      return [];
    }

    const sessions = parsed.sessions.filter(isUploadResourceSession);
    return pruneSessions(sessions);
  } catch {
    return [];
  }
};

/**
 * Persists undismissed upload sessions for up to {@link UPLOAD_SESSION_RETENTION_MS}.
 * Active/in-progress sessions are always kept; file bytes are not stored.
 */
export const savePersistedUploadSessions = (
  sessions: UploadResourceSession[]
): void => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  try {
    const toPersist = pruneSessions(
      sessions.filter(
        (session) =>
          !session.dismissed && isUploadSessionWithinRetention(session)
      )
    );

    const payload: PersistedUploadSessionsPayload = {
      version: 1,
      sessions: toPersist,
    };

    localStorage.setItem(UPLOAD_SESSIONS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private mode — ignore; in-memory transfers still work for the session.
  }
};

export { UPLOAD_SESSION_RETENTION_MS };
